import { useEffect, useRef, useState } from 'react';

import { useTranslations } from 'next-intl';

import { ActionIcon, Box, ScrollArea, Stack, Text } from '@mantine/core';
import { IconArrowDown } from '@tabler/icons-react';

import { ChatMessage, ChatRole } from '@repo/contracts';

import { ChatMessageBubble } from '@components/chatbot/ChatMessageBubble';
import { ToolRenderer } from '@components/chatbot/ToolRenderer';

interface ChatMessagesProps {
    messages: ChatMessage[];
    isLoading: boolean;
    streamingText: string;
    onEditLastMessage: (newContent: string) => void;
    isExecutingTool?: boolean;
    executingToolLabel?: string | null;
    onActionClick?: (key: string, label: string) => void;
    size?: 'sm' | 'md';
}

export function ChatMessages({ messages, isLoading, streamingText, onEditLastMessage, isExecutingTool = false, executingToolLabel = null, onActionClick, size = 'md' }: ChatMessagesProps) {
    const t = useTranslations();
    const scrollRef = useRef<HTMLDivElement>(null);
    const lastMessageRef = useRef<HTMLDivElement>(null);
    
    const isNearBottomRef = useRef(true); 
    const prevMessagesLengthRef = useRef(messages.length);
    const wasStreamingRef = useRef(false);
    
    const [showScrollBottom, setShowScrollBottom] = useState(false);

    useEffect(() => {
        const viewport = scrollRef.current;
        if (!viewport) return;

        const handleScroll = () => {
            const distanceToBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;            
            const nearBottom = distanceToBottom < 50; 
            isNearBottomRef.current = nearBottom;
            setShowScrollBottom(!nearBottom); 
        };

        viewport.addEventListener('scroll', handleScroll);
        return () => viewport.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const viewport = scrollRef.current;
        const lastMsg = lastMessageRef.current;
        if (!viewport) return;

        const isNewMessage = messages.length > prevMessagesLengthRef.current;
        prevMessagesLengthRef.current = messages.length;

        const isStreamingStart = !!streamingText && !wasStreamingRef.current;
        wasStreamingRef.current = !!streamingText;

        const forceScroll = isNewMessage || isStreamingStart || isExecutingTool;

        if (!isNearBottomRef.current && !forceScroll) return;

        const maxScroll = viewport.scrollHeight - viewport.clientHeight;
        let targetScroll = maxScroll;

        if (lastMsg) {
            const msgTop = lastMsg.offsetTop - 10;
            if (viewport.scrollTop <= msgTop + 50) {
                targetScroll = Math.min(maxScroll, msgTop);
            }
        }

        viewport.scrollTo({ top: targetScroll, behavior: 'smooth' });
    }, [messages, streamingText, isExecutingTool]);

    const visibleMessages = messages.filter((m) => m.role !== 'tool' && (m.content !== '' || (m.toolCalls && m.toolCalls.length > 0)));
    const lastUserMsgIndex = visibleMessages.map((m) => m.role).lastIndexOf('user');

    return (
        <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            
            <ScrollArea 
                style={{ flex: 1 }} 
                pl="md" 
                pr="md" 
                viewportRef={scrollRef}
                styles={{
                    viewport: {
                        maskImage: 'linear-gradient(to bottom, transparent 0%, black 20px, black calc(100% - 20px), transparent 100%)',
                        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 20px, black calc(100% - 20px), transparent 100%)',
                    },
                }}
            >
                <Stack gap="sm" pt={ size === 'md' ? 70 : 30} pb={40}>
                    <ChatMessageBubble 
                        role="assistant" 
                        content={t('chatbot-welcome-message')}
                        isLastUserMessage={false}
                        size={size}
                    />
                    
                    {visibleMessages.map((msg, index) => {
                        const isLast = !streamingText && index === visibleMessages.length - 1;
                        const isLastUser = index === lastUserMsgIndex;
                        const hasToolCalls = msg.toolCalls && msg.toolCalls.length > 0;
                        
                        return (
                            <Stack key={index} gap="xs">
                                {(msg.content || (msg.attachments && msg.attachments.length > 0)) && (
                                    <ChatMessageBubble 
                                        ref={isLast && !hasToolCalls ? lastMessageRef : null} 
                                        role={msg.role as ChatRole} 
                                        content={msg.content}
                                        attachments={msg.attachments}
                                        isLastUserMessage={isLastUser}
                                        onEditSubmit={onEditLastMessage} 
                                        onActionClick={onActionClick}
                                        size={size}
                                    />
                                )}
                                {hasToolCalls && msg.toolCalls?.map((tc, tcIndex) => (
                                    <ToolRenderer 
                                        key={tcIndex} 
                                        toolCall={tc} 
                                        onAction={onActionClick}
                                    />
                                ))}
                            </Stack>
                        );
                    })}
                    
                    {/* Loader (réflexion ou exécution d'outil) */}
                    {(isLoading || isExecutingTool) && !streamingText && (
                        <Stack ref={lastMessageRef} h={160} align="center" justify="center" gap="xs">
                            <Box style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }} >
                                <video src="/images/logo.webm" width={60} height={60} loop autoPlay muted />
                            </Box>
                            <Text size="sm" fw={500} c="nli-liberty.7">
                                {isExecutingTool ? t('processing-in-progress') : t('chatbot-thinking')}
                            </Text>
                            
                            {/* Espace fixe réservé pour le sous-titre */}
                            <Box h={18} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                                {isExecutingTool && executingToolLabel && (
                                    <Text size="xs" c="dimmed" fs="italic">
                                        {executingToolLabel}
                                    </Text>
                                )}
                            </Box>
                        </Stack>
                    )}

                    {streamingText && (
                        <ChatMessageBubble 
                            ref={lastMessageRef} 
                            role="assistant" 
                            content={streamingText}
                            size={size}
                        />
                    )}
                </Stack>
            </ScrollArea>

            {showScrollBottom && (
                <ActionIcon
                    onClick={() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })}
                    style={{
                        position: 'absolute', bottom: 10, right: '50%', transform: 'translateX(50%)', zIndex: 10,
                    }}
                    radius="xl"
                    variant="default"
                    size="lg"
                >
                    <IconArrowDown size={20} color="gray" />
                </ActionIcon>
            )}
        </div>
    );
}